/**
 * EventsList component for displaying and filtering a list or grid of events
 * Following the pattern of WorkingGroupsList with filters
 */

'use client';

import { Pagination } from '@/components/ui/pagination';
import { useEventsFilter } from '../hooks/use-events-filter';
import type { EventTeaser } from '../types';
import { EventCard } from './event-card';
import { EventsFilters } from './events-filters';

interface EventsListProps {
  /** Array of events to display */
  events: EventTeaser[];
  /** Base URL for event links */
  baseUrl?: string;
  /** Layout variant */
  variant?: 'default' | 'compact' | 'featured';
  /** Show past event indicator */
  showPastIndicator?: boolean;
  /** Page size for pagination */
  pageSize?: number;
}

/**
 * EventsList component displays a responsive grid of events with filters
 */
export function EventsList({
  events,
  baseUrl = '/events',
  variant = 'default',
  showPastIndicator = true,
  pageSize = 12,
}: EventsListProps) {
  const {
    query,
    selectedEventTypes,
    selectedThemes,
    themeFilterMode,
    showPastEvents,
    currentPage,
    eventTypes,
    themes,
    visibleEvents,
    totalResults,
    hasResults,
    hasActiveFilters,
    totalPages,
    startIndex,
    endIndex,
    normalizedQuery,
    setQuery,
    toggleEventType,
    toggleTheme,
    clearEventTypeSelection,
    clearThemeSelection,
    setThemeFilterMode,
    setShowPastEvents,
    handlePageChange,
    clearAllFilters,
  } = useEventsFilter({ events, pageSize });

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px] overflow-x-hidden">
      {/* Events Grid */}
      <div className="lg:col-start-1 min-w-0">
        {/* Main Content */}
        <main className="space-y-6 min-w-0">
          {/* Results Summary */}
          {hasResults && (
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <p>
                Showing {startIndex + 1}–{endIndex} of {totalResults}{' '}
                {totalResults === 1 ? 'event' : 'events'}
              </p>
            </div>
          )}

          {/* Events Grid */}
          {hasResults ? (
            <>
              <ul className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {visibleEvents.map(event => (
                  <li key={event.id} className="min-w-0 w-full">
                    <EventCard
                      event={event}
                      baseUrl={baseUrl}
                      variant={variant}
                      clickable={false}
                      showPastIndicator={showPastIndicator}
                    />
                  </li>
                ))}
              </ul>

              {/* Pagination */}
              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              )}
            </>
          ) : (
            <div className="flex min-h-[300px] flex-col items-centre justify-centre rounded-lg border border-dashed bg-muted/20 p-8 text-centre">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="mb-4 h-12 w-12 text-muted-foreground/50"
              >
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              <p className="text-lg font-medium text-muted-foreground">
                {hasActiveFilters ? 'No events match your filters.' : 'No events available.'}
              </p>
            </div>
          )}
        </main>
      </div>

      {/* Filters Sidebar */}
      <aside className="w-full lg:col-start-2 lg:row-start-1 lg:w-80 lg:justify-self-end min-w-0">
        <EventsFilters
          query={query}
          onQueryChange={setQuery}
          eventTypes={eventTypes}
          selectedEventTypes={selectedEventTypes}
          onToggleEventType={toggleEventType}
          onClearEventTypeSelection={clearEventTypeSelection}
          themes={themes}
          selectedThemes={selectedThemes}
          onToggleTheme={toggleTheme}
          onClearThemeSelection={clearThemeSelection}
          themeFilterMode={themeFilterMode}
          onThemeFilterModeChange={setThemeFilterMode}
          showPastEvents={showPastEvents}
          onShowPastEventsChange={setShowPastEvents}
          totalResults={totalResults}
          hasActiveFilters={hasActiveFilters}
          onClearAllFilters={clearAllFilters}
        />
      </aside>
    </div>
  );
}

